import React from 'react'
import { Alert, AlertIcon, Button, CloseButton, HStack, Text, VStack, useToast } from '@chakra-ui/react'
import { useLocation } from 'react-router-dom'
import { useFirebaseMessaging } from 'hooks/useFirebaseMessaging'
import { useStores } from 'hooks/useStores'
import routes from 'routes'

/**
 * Banner component that prompts users to enable notifications
 * Shows when notification permission is default or denied
 * Only displays on dashboard page
 */
const NotificationPermissionBanner: React.FC = () => {
  const { authStore } = useStores()
  const location = useLocation()
  const { isSupported, permissionStatus, requestPermission, refreshToken } = useFirebaseMessaging()

  const toast = useToast()
  const [isVisible, setIsVisible] = React.useState(true)
  const [isRequesting, setIsRequesting] = React.useState(false)

  // Only show if user is logged in, FCM is supported, permission is not granted,
  // banner is visible, and current route is dashboard
  const shouldShow =
    !!authStore.userDetail?.id &&
    isSupported &&
    permissionStatus !== 'granted' &&
    isVisible &&
    location.pathname === routes.dashboard.value

  if (!shouldShow) {
    return null
  }

  const handleRequestPermission = async () => {
    setIsRequesting(true)
    try {
      const granted = await requestPermission()
      if (granted) {
        // Refresh token to ensure it's saved
        await refreshToken()
        toast({
          title: 'Notifications enabled!',
          description: 'You will now receive real-time message notifications.',
          status: 'success',
          duration: 5000,
          isClosable: true
        })
        setIsVisible(false)
      } else {
        toast({
          title: 'Notifications blocked',
          description: 'You can enable notifications later in your browser settings.',
          status: 'warning',
          duration: 7000,
          isClosable: true
        })
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error)
      toast({
        title: 'Failed to enable notifications',
        description: 'Please try again or check your browser settings.',
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    } finally {
      setIsRequesting(false)
    }
  }

  const getAlertStatus = () => {
    switch (permissionStatus) {
      case 'denied':
        return 'error'
      case 'default':
        return 'info'
      default:
        return 'warning'
    }
  }

  const getMessageText = () => {
    switch (permissionStatus) {
      case 'denied':
        return 'Notifications are blocked. Enable them in your browser settings to receive real-time message alerts.'
      case 'default':
        return 'Enable notifications to receive real-time message alerts and stay up-to-date.'
      default:
        return 'Notification permission status unknown. Click to enable notifications.'
    }
  }

  const getButtonText = () => {
    switch (permissionStatus) {
      case 'denied':
        return 'How to Enable'
      case 'default':
        return 'Enable Notifications'
      default:
        return 'Try Again'
    }
  }

  const handleButtonClick = () => {
    if (permissionStatus === 'denied') {
      // Show instructions for enabling in browser settings
      toast({
        title: 'How to enable notifications',
        description: 'Click the lock icon in your address bar, then allow notifications for this site.',
        status: 'info',
        duration: 10000,
        isClosable: true
      })
    } else {
      handleRequestPermission()
    }
  }

  return (
    <Alert status={getAlertStatus()} borderRadius="md" mb={4} mt={4}>
      <AlertIcon />
      <VStack align="start" flex="1" spacing={2}>
        <Text fontSize="sm" mb={0}>
          {getMessageText()}
        </Text>
        <HStack spacing={2}>
          <Button
            size="sm"
            colorScheme={getAlertStatus() === 'error' ? 'red' : 'blue'}
            border="unset"
            boxShadow="unset"
            _focus={{ borderColor: 'unset' }}
            _active={{ borderColor: 'unset' }}
            onClick={handleButtonClick}
            isLoading={isRequesting}
          >
            {getButtonText()}
          </Button>
        </HStack>
      </VStack>
      <CloseButton
        border="unset"
        boxShadow="unset"
        _focus={{ borderColor: 'unset' }}
        _active={{ borderColor: 'unset' }}
        alignSelf="flex-start"
        position="relative"
        right={-1}
        top={-1}
        onClick={() => setIsVisible(false)}
      />
    </Alert>
  )
}

export default NotificationPermissionBanner
